import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CustomerService } from '../../services/customer.service';
import { Observable } from 'rxjs';

/**
 * Displays customer profile information for a given appointment. When the appointment input changes and contains a customer_id, the component loads the customer's profile via CustomerService and exposes it as an Observable for template binding. Includes a helper to compute a displayable full name with fallbacks for backward compatibility.
 * @component
 * @selector app-customer-information
 * @example
 *   <app-customer-information [appointment]="sampleAppointment"></app-customer-information>
 * @input any appointment - Appointment object that should include customer_id; used to trigger fetching of the associated customer profile when changed.
 * @output none - This component does not emit events; it exposes a public customer$ Observable for templates to subscribe to or async-bind.
 * Key properties:
 *   - customer$: Observable<any> | undefined - Observable that emits the loaded customer profile.
 * Key methods:
 *   - getCustomerFullName(customerData: any): string - Returns a human-readable full name for the customer. Handles legacy shapes (name) and constructs "first_name last_name" when available, with sensible fallbacks.
 * Lifecycle hooks:
 *   - ngOnChanges(changes: SimpleChanges): void - Watches the appointment input and, when a valid customer_id is present, calls CustomerService.getCustomerProfile to populate customer$.
 */
@Component({
  selector: 'app-customer-information',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './customer-information.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./customer-information.component.scss'],
})
export class CustomerInformationComponent implements OnChanges {
  @Input() appointment: any;
  customer$: Observable<any> | undefined;

  constructor(private customerService: CustomerService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appointment'] && this.appointment && this.appointment.customer_id) {
      this.customer$ = this.customerService.getCustomerProfile(this.appointment.customer_id);
    }
  }

  getCustomerFullName(customerData: any): string {
    if (!customerData) return '';
    if (customerData.name) return customerData.name; // Fallback for backward compatibility
    if (!customerData.first_name) return '';
    if (!customerData.last_name) return customerData.first_name;
    return `${customerData.first_name} ${customerData.last_name}`;
  }
}
